import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { SearchIcon, TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { IoMdAdd } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { DeviceTypeBox } from "./DeviceTypeBox";
import API from "../../context/API";

const DeviceTypes = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentDeviceType, setCurrentDeviceType] = useState(null);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [filteredDeviceTypes, setFilteredDeviceTypes] = useState([]);
  const toast = useToast();

  async function fetchDeviceTypes() {
    try {
      const deviceTypesResponse = await API.get("device-types", {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      const deviceTypesData = deviceTypesResponse.data.data;
      setDeviceTypes(deviceTypesData);
      setFilteredDeviceTypes(deviceTypesData);
    } catch (error) {
      toast({
        title: "Error fetching device types.",
        description: error.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }

  useEffect(() => {
    fetchDeviceTypes();
    return () => {
      setDeviceTypes([]);
    };
  }, []);

  const handleAddClick = () => {
    setCurrentDeviceType(null);
    setIsEditing(true);
  };

  const handleItemClick = (itemId) => {
    const deviceTypeToEdit = deviceTypes.find((item) => item.id === itemId);
    setCurrentDeviceType(deviceTypeToEdit);
    setIsEditing(true);
  };

  const handleDeleteClick = async (itemId) => {
    try {
      const deleteDeviceTypeResponse = await API.delete(
        `device-types/${itemId}`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );
      if (deleteDeviceTypeResponse.data.status === 200) {
        toast({
          title: "Success.",
          description: "Device type deleted successfully.",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        const updatedDeviceTypes = deviceTypes.filter(
          (item) => item.id !== itemId
        );
        setDeviceTypes(updatedDeviceTypes);
        setFilteredDeviceTypes(updatedDeviceTypes);
      } else {
        toast({
          title: "Error.",
          description: deleteDeviceTypeResponse.data.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error.",
        description: error.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const handleSearch = (event) => {
    const filter = event.target.value.toLowerCase();
    if (!filter) {
      setFilteredDeviceTypes(deviceTypes);
      return;
    }
    const filtered = deviceTypes.filter((item) =>
      item.name.toLowerCase().includes(filter)
    );
    setFilteredDeviceTypes(filtered);
  };
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "" });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    filteredDeviceTypes.sort((a, b) => {
      const aValue = a[key]?.toLowerCase();
      const bValue = b[key]?.toLowerCase();

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const getArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? <TriangleUpIcon/> :<TriangleDownIcon/>;
    }
    return "↕";
  };

  return (
    <>
      {!isEditing ? (
        <>
          <Box>
            <Flex
              justify={"space-between"}
              alignItems={"center"}
              width={"100%"}
              flexWrap={"wrap"}
              mt={4}
            >
              <Box>
                <Heading size={"xl"} letterSpacing={-2}>
                  Device Types
                </Heading>
              </Box>
              <Box>
                <Button
                  leftIcon={<IoMdAdd />}
                  bg="black"
                  color={"white"}
                  _hover={{
                    color: "black",
                    bg: "white",
                    borderWidth: "1px",
                    borderColor: "gray.300",
                  }}
                  variant="outline"
                  onClick={handleAddClick}
                >
                  Add Device Type
                </Button>
              </Box>
            </Flex>
            <Box m={4}>

              <InputGroup size={"md"} width={"50%"}>
                <InputLeftElement
                  pointerEvents="none"
                  children={<SearchIcon color="gray.300" />}
                />
                <Input
                  type="search"
                  placeholder="Search..."
                  _placeholder={{ color: "gray.500" }}
                  onKeyUp={handleSearch}
                />
              </InputGroup>
            </Box>
          </Box>
          <TableContainer>
            <Table
              variant="striped"
              colorScheme="gray"
              size="md"
              overflowX="auto"
            >

              <Thead bg="gray.200">
                <Tr>
                  <Th
                    onClick={() => handleSort("name")}
                    cursor="pointer"
                    textAlign="center"
                  >
                    Name {getArrow("name")}
                  </Th>
                  <Th textAlign="center">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredDeviceTypes.map((item) => (
                  <Tr
                    key={item.id}
                    _hover={{ bg: "gray.200" }}
                    transition="background-color 0.2s ease-in-out"
                  >
                    <Td textAlign="center">
                      <Link
                        color="teal.600"
                        fontWeight="medium"
                        _hover={{ textDecoration: "underline" }}
                        onClick={() => handleItemClick(item.id)}
                      >
                        {item.name}
                      </Link>
                    </Td>
                    <Td textAlign="center">
                      <Button
                        variant="solid"
                        colorScheme="red"
                        size="sm"
                        onClick={() => handleDeleteClick(item.id)}
                      >
                        <MdDelete />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <DeviceTypeBox
          initialDeviceType={currentDeviceType}
          onSave={() => setIsEditing(false)}
          onCancel={() => setIsEditing(false)}
          fetchDeviceTypes={fetchDeviceTypes}
        />
      )}
    </>
  );
};

export default DeviceTypes;
